import React, { useState } from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING, EMZI_SHADOWS } from '../../styles/emziCI';

/**
 * DynamicListInput Component - EMZI CI Flat v6.3
 * ===============================================
 * Dynamic list input with add/remove functionality
 * 
 * Features:
 * - Add items via "+ Add Field" button or Enter key
 * - Remove items inline
 * - Support for text and textarea types
 * - Compact spacing
 * - Flat border design
 */

const DynamicListInput = ({
  items = [],
  onChange,
  placeholder = 'Enter value...',
  label = 'Items',
  type = 'text', // 'text' | 'textarea'
  maxItems = 20,
  allowEmpty = false,
}) => {
  const [newItemValue, setNewItemValue] = useState('');

  const handleAddItem = () => {
    if (newItemValue.trim() && items.length < maxItems) {
      onChange([...items, newItemValue.trim()]);
      setNewItemValue('');
    }
  };

  const handleRemoveItem = (index) => {
    const updated = items.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleUpdateItem = (index, value) => {
    const updated = [...items];
    updated[index] = value;
    onChange(updated);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && type === 'text') {
      e.preventDefault();
      handleAddItem();
    }
  };

  const inputStyles = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: `1px solid ${EMZI_COLORS.border}`,
    borderRadius: EMZI_RADIUS.sm,
    outline: 'none',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  };

  const focusStyles = {
    borderColor: EMZI_COLORS.primary,
    boxShadow: `0 0 0 3px rgba(0, 114, 229, 0.1)`,
  };

  return (
    <div style={{ marginBottom: EMZI_SPACING.md }}>
      {/* Label */}
      <label
        style={{
          display: 'block',
          fontSize: '12px',
          fontWeight: '600',
          color: EMZI_COLORS.textPrimary,
          marginBottom: EMZI_SPACING.sm,
        }}
      >
        {label}
      </label>

      {/* Existing Items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: EMZI_SPACING.sm }}>
        {items.map((item, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              gap: EMZI_SPACING.sm,
              alignItems: type === 'text' ? 'center' : 'flex-start',
            }}
          >
            {type === 'text' ? (
              <input
                type="text"
                value={item}
                onChange={(e) => handleUpdateItem(index, e.target.value)}
                style={inputStyles}
                onFocus={(e) => Object.assign(e.target.style, focusStyles)}
                onBlur={(e) => {
                  e.target.style.borderColor = EMZI_COLORS.border;
                  e.target.style.boxShadow = 'none';
                }}
              />
            ) : (
              <textarea
                value={item}
                onChange={(e) => handleUpdateItem(index, e.target.value)}
                rows={2}
                style={{ ...inputStyles, resize: 'vertical', minHeight: '60px' }}
                onFocus={(e) => Object.assign(e.target.style, focusStyles)}
                onBlur={(e) => {
                  e.target.style.borderColor = EMZI_COLORS.border;
                  e.target.style.boxShadow = 'none';
                }}
              />
            )}
            <button
              onClick={() => handleRemoveItem(index)}
              style={{
                padding: '8px 12px',
                backgroundColor: EMZI_COLORS.error,
                color: EMZI_COLORS.textWhite,
                border: 'none',
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                flexShrink: 0,
                minWidth: '70px',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => (e.target.style.opacity = 0.9)}
              onMouseLeave={(e) => (e.target.style.opacity = 1)}
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      {/* Add New Item Input */}
      {items.length < maxItems && (
        <div
          style={{
            display: 'flex',
            gap: EMZI_SPACING.sm,
            marginTop: items.length > 0 ? EMZI_SPACING.md : 0,
            alignItems: type === 'text' ? 'center' : 'flex-start',
          }}
        >
          {type === 'text' ? (
            <input
              type="text"
              value={newItemValue}
              onChange={(e) => setNewItemValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              style={inputStyles}
              onFocus={(e) => Object.assign(e.target.style, focusStyles)}
              onBlur={(e) => {
                e.target.style.borderColor = EMZI_COLORS.border;
                e.target.style.boxShadow = 'none';
              }}
            />
          ) : (
            <textarea
              value={newItemValue}
              onChange={(e) => setNewItemValue(e.target.value)}
              placeholder={placeholder}
              rows={2}
              style={{ ...inputStyles, resize: 'vertical', minHeight: '60px' }}
              onFocus={(e) => Object.assign(e.target.style, focusStyles)}
              onBlur={(e) => {
                e.target.style.borderColor = EMZI_COLORS.border;
                e.target.style.boxShadow = 'none';
              }}
            />
          )}
          <button
            onClick={handleAddItem}
            disabled={!newItemValue.trim()}
            style={{
              padding: '8px 16px',
              backgroundColor: newItemValue.trim() ? EMZI_COLORS.accent : EMZI_COLORS.border,
              color: EMZI_COLORS.textWhite,
              border: 'none',
              borderRadius: EMZI_RADIUS.sm,
              fontSize: '12px',
              fontWeight: '600',
              cursor: newItemValue.trim() ? 'pointer' : 'not-allowed',
              flexShrink: 0,
              minWidth: '80px',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) =>
              newItemValue.trim() && (e.target.style.backgroundColor = EMZI_COLORS.accentHover)
            }
            onMouseLeave={(e) =>
              newItemValue.trim() && (e.target.style.backgroundColor = EMZI_COLORS.accent)
            }
          >
            + Add
          </button>
        </div>
      )}

      {/* Helper Text */}
      <p
        style={{
          fontSize: '11px',
          color: EMZI_COLORS.textMuted,
          marginTop: EMZI_SPACING.sm,
        }}
      >
        {type === 'text' && 'Press Enter or click "+ Add" to add items.'}
        {items.length >= maxItems && ` Maximum ${maxItems} items reached.`}
      </p>
    </div>
  );
};

export default DynamicListInput;
